import React, { useEffect, useRef } from 'react';
import DecisionOutcomeCard from './DecisionOutcomeCard';
import { Info, Mail, MailOutline, Forum, Warning, TrendingUp, CheckCircle } from '@mui/icons-material';

export interface FeedEntry {
    id: string;
    type: 'system' | 'decision_outcome' | 'email_received' | 'email_sent' | 'slack_message' | 'deadline_warning' | 'escalation' | 'comms_resolved_decision';
    timestamp: string;
    // For system entries
    message?: string;
    // For decision_outcome
    prompt?: string;
    chosenText?: string;
    feedback?: string;
    // For email entries
    from?: string;
    subject?: string;
    // For slack entries
    channel?: string;
    content?: string;
    // For deadline/escalation entries
    description?: string;
    // For comms_resolved_decision
    npcName?: string;
    decisionPrompt?: string;
}

interface ActivityFeedProps {
    entries: FeedEntry[];
}

const formatTime = (ts: string): string => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({ entries }) => {
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [entries.length]);

    return (
        <div className="flex-1 overflow-y-auto space-y-3 pr-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {entries.map((entry) => {
                switch (entry.type) {
                    case 'system':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-3 py-2.5 bg-slate-900/50 rounded-lg">
                                <Info className="text-blue-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-slate-300 text-sm">{entry.message}</p>
                                </div>
                            </div>
                        );

                    case 'decision_outcome':
                        return (
                            <DecisionOutcomeCard
                                key={entry.id}
                                prompt={entry.prompt || ''}
                                chosenText={entry.chosenText || ''}
                                feedback={entry.feedback || ''}
                            />
                        );

                    case 'email_received':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-3 py-2 bg-slate-900/40 rounded-lg">
                                <Mail className="text-amber-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-slate-300 text-sm">
                                        <span className="text-amber-400 font-medium">{entry.from}</span> — {entry.subject}
                                    </p>
                                </div>
                            </div>
                        );

                    case 'email_sent':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-3 py-2 bg-slate-900/40 rounded-lg">
                                <MailOutline className="text-blue-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-slate-300 text-sm">
                                        You emailed <span className="text-blue-400 font-medium">{entry.from}</span> — {entry.subject}
                                    </p>
                                </div>
                            </div>
                        );

                    case 'slack_message':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-3 py-2 bg-slate-900/40 rounded-lg">
                                <Forum className="text-purple-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-slate-300 text-sm">
                                        <span className="text-purple-400 font-medium">#{entry.channel}</span> — {entry.content}
                                    </p>
                                </div>
                            </div>
                        );

                    case 'deadline_warning':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-2 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                                <Warning className="text-amber-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-amber-300 text-sm font-medium">{entry.description}</p>
                                </div>
                            </div>
                        );

                    case 'escalation':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-2 py-2 bg-red-500/10 rounded-lg border border-red-500/20">
                                <TrendingUp className="text-red-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-red-300 text-sm font-medium">{entry.description}</p>
                                </div>
                            </div>
                        );

                    case 'comms_resolved_decision':
                        return (
                            <div key={entry.id} className="flex items-start gap-3 px-2 py-2 bg-green-500/10 rounded-lg border border-green-500/20">
                                <CheckCircle className="text-green-400 shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                                <div>
                                    <span className="text-[10px] text-slate-500 font-mono">{formatTime(entry.timestamp)}</span>
                                    <p className="text-green-300 text-sm font-medium">
                                        Your email to <span className="text-green-400">{entry.npcName}</span> resolved: {entry.decisionPrompt}
                                    </p>
                                </div>
                            </div>
                        );

                    default:
                        return null;
                }
            })}
            <div ref={bottomRef} />
        </div>
    );
};

export default ActivityFeed;
